package com.nonopn.skydome.common.app.pages.documents

import com.nonopn.skydome.common.app.pages.main.model.DocumentHistoryItem
import com.nonopn.skydome.common.app.pages.probes.matchesSearch

// Pure helpers of the compact site list of the "Reports & Certificates" screen: which sites are listed,
// in which order, and how they are paginated. No UI nor network here.

/** Number of sites displayed at first, and added by each "Show more". */
const val SitesPageSize = 20

/** A site (project) of the account, as far as this screen is concerned. */
data class KnownSite(
    val uuid: String,
    val name: String,
    val latitude: Double?,
    val longitude: Double?,
)

/**
 * A row of the list: a site and its documents (possibly none). [projectUuid] is null for a site only known
 * through its documents (not a project of the account anymore).
 */
data class SiteEntry(
    val key: String,
    val name: String,
    val latitude: Double?,
    val longitude: Double?,
    val projectUuid: String?,
    val documents: List<DocumentHistoryItem>,
)

/** One page of the list: the visible entries and the number of entries still hidden. */
data class SitesPage(
    val visible: List<SiteEntry>,
    val remaining: Int,
)

/**
 * Rows of the list: the sites having documents (already filtered by year and query, most recent document first),
 * followed, only when [query] is not blank, by the matching sites without any document.
 */
fun buildSiteEntries(
    known: List<KnownSite>,
    sites: List<SiteDocuments>,
    query: String,
): List<SiteEntry> {
    val byUuid = known.associateBy { it.uuid }
    val withDocuments = sites.map { site ->
        val project = byUuid[site.key]

        SiteEntry(
            key = site.key,
            name = project?.name ?: site.siteName,
            latitude = project?.latitude ?: site.latitude,
            longitude = project?.longitude ?: site.longitude,
            projectUuid = project?.uuid,
            documents = site.documents,
        )
    }

    if (query.isBlank()) {
        return withDocuments
    }

    val documentKeys = withDocuments.map { it.key }.toSet()
    val withoutDocuments = filterKnownSites(known, query)
        .filterNot { documentKeys.contains(it.uuid) }
        .map { project ->
            SiteEntry(
                key = project.uuid,
                name = project.name,
                latitude = project.latitude,
                longitude = project.longitude,
                projectUuid = project.uuid,
                documents = emptyList(),
            )
        }

    return withDocuments + withoutDocuments
}

/** Sites whose name contains [query] (all of them when blank), sorted by name for the "all sites" list. */
fun filterKnownSites(known: List<KnownSite>, query: String): List<KnownSite> = known
    .filter { query.isBlank() || matchesSearch(query, it.name) }
    .sortedWith(compareBy<KnownSite> { it.name.lowercase() }.thenBy { it.uuid })

/** The first [visibleCount] entries and how many remain. */
fun pageOfSites(entries: List<SiteEntry>, visibleCount: Int): SitesPage {
    val count = visibleCount.coerceAtLeast(0)

    return SitesPage(
        visible = entries.take(count),
        remaining = (entries.size - count).coerceAtLeast(0),
    )
}

/** Visible count after a tap on "Show more". */
fun nextVisibleCount(current: Int): Int = current + SitesPageSize
